// Copyright (c) Docugami, Inc. All rights reserved.

import { useRef } from 'react';
import styled from 'styled-components';
import { DirectionalHint, TooltipHost } from "@fluentui/react";
import { dgFontFamily, SmallButton } from 'components/common/Fonts';
import { greyText } from 'components/shell/presentation/AppStyles';

/** Metrics status bar UX */

export enum StatusType {
    Human = "Human",
    Machine = "Machine",
    MachineConfirmed = "Confirmed",
    Missing = "Missing"
}

const StatusBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    height: 20px;
    padding: 2px;
    gap: 2px;
    background-color: white;
    border: 1px solid ${`${greyText}80`};
    border-radius: 3px;
    box-sizing: border-box;
`;

const StatusItem = styled.div<{ percentage: number, color?: string, textHidden?: boolean }>`
    display: flex;
    flex: ${props => props.percentage};
    min-width: 0;
    text-align: center;
    justify-content: center;
    align-items: center;
    font-size: 10px;
    overflow: hidden;
    background-color: ${props => props.color ?? 'none'};
    box-sizing: border-box;
    transition: all 0.7s ease-in-out;
    ${props => !!props.textHidden && 'color: rgba(0, 0, 0, 0);'}
    font-family: ${dgFontFamily};

    :first-of-type {
        border-top-left-radius: 2px;
        border-bottom-left-radius: 2px;
    }

    :last-of-type {
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;
    }
`;

export interface StatusBarItem {
    name: string;
    color: string;
    percentage: number;
}

export interface StatusBarProps {
    items: StatusBarItem[];
    hidden?: boolean;
}

const hidePercentageBoundary = 5;

export const MetricsStatusBar = (props: StatusBarProps): JSX.Element => {
    const statusBarRef = useRef<HTMLDivElement>(null);

    return (
        <StatusBarContainer ref={statusBarRef} hidden={!!props.hidden} >
            {
                props.items.map(item => {
                    if (item.percentage === 0) {
                        return null;
                    }

                    const percentageHidden = item.percentage < hidePercentageBoundary;

                    return (
                        <StatusItem key={item.name} percentage={item.percentage} color={item.color} textHidden={percentageHidden} >
                            {!!item.color &&
                                <TooltipHost
                                    style={{ display: 'inline-flex' }}
                                    content={percentageHidden ? `${item.percentage}%` : ''}
                                    directionalHint={DirectionalHint.bottomCenter} >
                                    {item.percentage}%
                                </TooltipHost>}
                        </StatusItem>
                    )
                })
            }
        </StatusBarContainer>
    );
}


/** Legend for the metrics status bar UX */

const ColorCircle = styled.div<{ color: string }>`
    display: inline-block;
    height: 1em;
    width: 1em;
    background-color: ${props => props.color};
    ${props => !props.color && 'border: 1px solid #D0CDCD'};
    box-sizing: border-box;
`;

const LegendItemText = styled.span`
    font-size: 1em;
    font-family: ${dgFontFamily};
    margin-left: 5px;
`;

const LegendItem = styled.div`
    display: flex;
    font-size: 10px;
    align-items: center;
    margin: 5px 0;
`;

const LegendContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const LegendItemsContainer = styled.div`
    display: flex;
    gap: 20px;
    :only-child {
        margin: 0 auto;
    }
`;

const LegendTitle = styled(SmallButton)`
    font-size: 14px;
`;

interface MetricsLegendProps {
    keys: StatusType[];
    getUxUtils: (key: StatusType) => { name: string, color: string };
    title?: string;
}

export const MetricsLegend = (props: MetricsLegendProps): JSX.Element => {
    return (
        <LegendContainer>
            {!!props.title && <LegendTitle>{props.title.toUpperCase()}</LegendTitle>}
            <LegendItemsContainer>
                {
                    props.keys.map(key => {
                        const { name, color } = props.getUxUtils(key);
                        return (
                            <LegendItem key={name}>
                                <ColorCircle color={color} />
                                <LegendItemText>{name.toLocaleUpperCase()}</LegendItemText>
                            </LegendItem>
                        )
                    })
                }
            </LegendItemsContainer>
        </LegendContainer>
    );
}
